import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { del } from "@/utils/api-handler";
import { toast } from "sonner";
import { DeleteConfirmationDialog } from "@/components/shared/delete-dialog";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { PermissionGate } from "@/components/shared/permission-gate";
import { useProtectedMutation } from "@/hooks/use-protected-mutation";
import { MoreHorizontal, Trash2, ExternalLink, File } from "lucide-react";

interface ActionCellProps {
    row: { original: DocumentFile };
    setActiveSection?: React.Dispatch<React.SetStateAction<string>>;
    invalidateQueryKeys?: string[][];
}

export const DocumentsActionCell: React.FC<ActionCellProps> = ({
    row,
    setActiveSection,
    invalidateQueryKeys = [["documents-files"]],
}) => {
    const queryClient = useQueryClient();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const deleteMutation = useProtectedMutation({
        mutationFn: (documentId: string) => {
            return del(`/api/documents/files/${documentId}`);
        },
        requiredPermissions: ["DELETE_DOCUMENT"],
        onSuccess: () => {
            invalidateQueryKeys.forEach((queryKey) => {
                queryClient.invalidateQueries({ queryKey });
            });
            toast.success("Document deleted successfully", {
                description: "The document has been removed.",
            });
            setDeleteDialogOpen(false);
        },
        onError: (error: any) => {
            toast.error("Failed to delete document", {
                description:
                    error.response?.data?.message || "An error occurred while deleting",
            });
            setDeleteDialogOpen(false);
        },
    });

    const handleDelete = async () => {
        try {
            await deleteMutation.mutateAsync(row.original.id);
        } catch (error) {
            console.error("Delete operation failed:", error);
        }
    };

    return (
        <div className="flex items-center justify-end">
            <DeleteConfirmationDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                onConfirm={handleDelete}
                isLoading={deleteMutation.isPending}
                itemName={row.original.name}
                itemType="document"
            />
            <div className="flex flex-col items-end">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[160px]">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            onClick={() => {
                                const url = row.original.url;
                                if (url) {
                                    window.open(url, "_blank");
                                }
                            }}
                        >
                            <ExternalLink className="mr-2 h-4 w-4" />
                            Download
                        </DropdownMenuItem>
                        {row.original.transcription_url && (
                            <DropdownMenuItem
                                onClick={() => {
                                    const url = row.original.transcription_url;
                                    if (url) {
                                        window.open(url, "_blank");
                                    }
                                }}
                            >
                                <File className="mr-2 h-4 w-4" />
                                View Transcription
                            </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <PermissionGate requiredPermissions={["DELETE_DOCUMENT"]}>
                            <DropdownMenuItem
                                className="text-destructive focus:text-destructive cursor-pointer"
                                onClick={() => setDeleteDialogOpen(true)}
                                disabled={deleteMutation.isPending}
                            >
                                <Trash2 className="mr-2 h-4 w-4" />
                                {deleteMutation.isPending ? "Deleting..." : "Delete Document"}
                            </DropdownMenuItem>
                        </PermissionGate>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
    );
};

